import * as React from 'react';
import { connect } from 'react-redux';
import V3CollapsibleSection from '../../components/V3CollapsibleSection';
import V3SpecsTable from '../../components/V3SpecsTable';
import V3SpecRow from '../../components/V3SpecRow';
import V3SpecFieldWrap from '../../components/V3SpecFieldWrap';
import V3UnitToggle from '../../components/V3UnitToggle';
import V3Button from '../../components/V3Button';
import { SET_PROJECT_OPTION, RESET_PANELS_STATE, BOM_RESET } from 'actions';
import { SET_ROW_SPACING, SET_COLUMN_SPACING } from '__editor/panelsEditor/actions';
import { drawRoofZonesOnStage } from '__editor/panelsEditor/components/roofZones/drawRoofZonesOnStage';
import { removePanelsInsideSetbackDistance } from '__editor/panelsEditor/components/panels/utils/panelsManagment';
import { SET_ROOF_ROOF_PITCH, DELETE_PANELS_INSIDE_OF_ROOF } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManagerActions';
import { UPDATE_LITE_PROJECT_REQUEST } from 'v3/dashboard/liteProjectActions';
import { anyPanelsDrawn } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManagerHelper';
import { state as getState } from '__common/store';
import { SET_TILTED_ROOF } from '__editor/panelsEditor/components/tiltedRoof/tiltedRoofActions';
import { AttachmentOptimization } from 'projectDesign/components/projectConfiguration/fields/attachmentOptimizationCriteria';
import { MidEndClampChoice } from 'projectDesign/components/projectConfiguration/utils/constants';
import { isASCE716or722 } from '__common/constants/buildingCodes';
import { inchesToMeters } from '__common/calculations/inchesToMeters';
import { MAP_TYPE } from '__common/constants/map';
import {
  isRM10Evolution,
  isSFMFamily,
  isMetalX,
  isSMTiltPR,
  isSunframeMicroRail,
  isSMFlushOrTiltOrTiltPR,
  isNxtHorizon,
  isSM2Standard,
  isSolarMount,
  isSMAscenderFlush,
} from '__common/constants/products';
import {
  getBuildingHeightLimits,
  getShortestBuildingLengthLimits,
  getLongestBuildingLengthLimits,
  getRafterSpacingLimits,
  getSetbackLimits,
} from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/utils/validateProject';

type Props = {
  dispatch: Function;
  systemType: string;
  roofType: number | string;      // from step 4 handoff; used to pick rafter-spacing default
  buildingHeight: number | undefined;
  shortestBuildingLength: number | undefined;
  longestBuildingLength: number | undefined;
  staggerAttachments: boolean;
  parapetHeight: number | string | undefined;
  // Flat + shared
  tilt: number | undefined;
  setbackDistance: number | undefined;
  blockWeight: number | undefined;
  attachmentOptimizationCriteria: number | string | undefined;
  // Pitched-specific
  roofPitch: string;              // "1/12" ... "12/12"
  railDirection: string;          // "EW" | "NS"
  rafterSpacingInches: number | undefined;
  preferredSpan: number | undefined;
  // RM-family advanced flags. allow_north_bays is PositiveSmallIntegerField
  // with choices=YES_OR_NO on the backend — dispatched as int 1/0 (the
  // serializer rejects boolean true/false). mapStateToProps coerces back to
  // a stable boolean for the toggle's visual state. is_half_block_allowed is
  // BooleanField; half_block_weight is DecimalField (lbs).
  productId: number | undefined;
  allowNorthBays: boolean | undefined;
  domesticContent: boolean | undefined;
  isHalfBlockAllowed: boolean | undefined;
  halfBlockWeight: number | undefined;
  panelCount: number;
  activeRoofId: number | null;
  liteRoofPitch: number | undefined;
  envRoofPitch: number | undefined;
  // Passed down from DesignWorkspace so the stagger toggle can fire an
  // immediate BOM regen (mirrors BomPanel's attachment dropdowns).
  projectId?: string;
  liteUpdating?: boolean;
  flatRoofPitch: number | undefined;
  midClampsChoices: number | undefined;
  endClampsChoices: number | undefined;
  buildingCode: number | undefined;
  // Satellite (Google Maps) roof-marking view with no active roof selected:
  // the user isn't inside a roof yet, so per-roof pitch is meaningless here.
  hideRoofPitch: boolean;
  forceOpenSignal?: number;
  domesticClampTarget?: number | null;
  onDomesticClampResolved?: (confirmed: boolean) => void;
  onSwitchDomesticOff?: () => void;
};

type AdvancedState = {
  pendingRoofPitch: string | null;
  pendingTilt: number | null;
  pendingMidClamp: number | null;
  pendingFromDomestic: boolean;
  pendingDomesticOff: boolean;
  pendingEndClamp: number | null;
};

// Metal-roof types that use 12" default rafter (rib/seam) spacing per the
// Lite defaults spec. All other pitched roof types use 24".
// 2 = STANDING_SEAM, 5 = R_PANEL (from RoofType enum in constants.ts)
const METAL_ROOF_TYPES: Array<number | string> = [2, '2', 5, '5'];
const defaultRafterSpacing = (roofType: number | string): number =>
  METAL_ROOF_TYPES.includes(roofType) ? 12 : 24;

// Pitched roof pitch — backend stores as "1/12"..."12/12" (see ROOF_PITCH in
// __editor/panelsEditor/components/tiltedRoof/roofPitch.ts)
const PITCHED_ROOF_PITCH_OPTIONS = [
    ...Array.from({ length: 45 }, (_, i) => {
      const degree = i + 1;
      const pitchDegrees: { [key: number]: string } = {
        5: '1/12',
        9: '2/12',
        14: '3/12',
        18: '4/12',
        22: '5/12',
        26: '6/12',
        30: '7/12',
        33: '8/12',
        36: '9/12',
        39: '10/12',
        42: '11/12',
        45: '12/12',
      };
      const pitch = pitchDegrees[degree];
      const name = pitch ? `${degree}° (${pitch})` : `${degree}°`;
      return { value: degree.toString(), name };
    })
  ];

// Flat roof pitch — lite-specific slope options (1°-7°) from v6 design.
// Maps to the `tilt` apiField used across the old system.
const FLAT_ROOF_PITCH_OPTIONS = [
  { value: 1, name: '1° Slope' },
  { value: 2, name: '2° Slope' },
  { value: 3, name: '3° Slope' },
  { value: 4, name: '4° Slope' },
  { value: 5, name: '5° Slope' },
];

// Flat attachment config — maps to AttachmentOptimization enum values
// (old `attachment_optimization_criteria` field).
const FLAT_ATTACHMENT_CONFIG_OPTIONS = [
  { value: AttachmentOptimization.MAXIMIZE_BALLAST, name: 'Min. Attachment' },
  { value: AttachmentOptimization.FULLY_ATTACHED, name: 'Fully Attached' },
];

// Rail direction — old `rail_direction` apiField: "EW" = Cross-Slope, "NS" = Up-Slope.
const RAIL_DIRECTION_OPTIONS = [
  { value: 'EW', name: 'Cross-Slope' },
  { value: 'NS', name: 'Up-Slope' },
];

// SM mid/end clamp options — mirrors legacy smClampsOptions.proStdUni.
const SM_CLAMP_OPTIONS = [
  { value: MidEndClampChoice.PRO_CLAMP, name: 'Pro Clamps' },
  { value: MidEndClampChoice.STANDARD_CLAMP, name: 'Standard Clamps' },
  { value: MidEndClampChoice.UNIVERSAL_CLAMP, name: 'Universal AF' },
];

// Ascender rail clamp options — mid clamp is Universal AF only; end clamp
// offers Pro and Universal AF (Universal AF selected by default).
const ASCENDER_MID_CLAMP_OPTIONS = [
  { value: MidEndClampChoice.UNIVERSAL_CLAMP, name: 'Universal AF' },
];

const ASCENDER_END_CLAMP_OPTIONS = [
  { value: MidEndClampChoice.PRO_CLAMP, name: 'Pro Clamps' },
  { value: MidEndClampChoice.UNIVERSAL_CLAMP, name: 'Universal AF' },
];

const AdvancedIcon = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="7" width="12" height="6" rx="1" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    <path d="M4 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

class AdvancedSection extends React.Component<Props, AdvancedState> {
  state: AdvancedState = { pendingRoofPitch: null, pendingTilt: null, pendingMidClamp: null, pendingFromDomestic: false, pendingDomesticOff: false, pendingEndClamp: null };

  getPreferredSpanLimits = (productId: number | undefined) => {
    if (!productId) return { min: 4, max: 144 };
    if (isSFMFamily(productId) || isMetalX(productId)) {
      return { min: 12, max: 72 };
    }
    if (isSMTiltPR(productId)) {
      return { min: 4, max: 72 };
    }
    return { min: 4, max: 144 };
  };

  computeErrors = (): {
    buildingHeight?: string;
    shortestBuildingLength?: string;
    longestBuildingLength?: string;
    preferredSpan?: string;
    rafterSpacingInches?: string;
    parapetHeight?: string;
    setbackDistance?: string;
    blockWeight?: string;
  } => {
    const {
      buildingHeight, shortestBuildingLength, longestBuildingLength,
      preferredSpan, rafterSpacingInches, parapetHeight, setbackDistance, blockWeight,
      productId
    } = this.props;

    const errs: {
      buildingHeight?: string;
      shortestBuildingLength?: string;
      longestBuildingLength?: string;
      preferredSpan?: string;
      rafterSpacingInches?: string;
      parapetHeight?: string;
      setbackDistance?: string;
      blockWeight?: string;
    } = {};

    if (buildingHeight !== undefined && buildingHeight !== null) {
      const limits = getBuildingHeightLimits();
      if (buildingHeight < limits.lowerLimit || buildingHeight > limits.upperLimit) {
        errs.buildingHeight = `Building height must lie between ${limits.lowerLimit} and ${limits.upperLimit} ft`;
      }
    }

    if (shortestBuildingLength !== undefined && shortestBuildingLength !== null) {
      const limits = getShortestBuildingLengthLimits();
      if (shortestBuildingLength < limits.lowerLimit || shortestBuildingLength > limits.upperLimit) {
        errs.shortestBuildingLength = `Shortest building length must lie between ${limits.lowerLimit} and ${limits.upperLimit} ft`;
      }
    }

    if (longestBuildingLength !== undefined && longestBuildingLength !== null) {
      const limits = getLongestBuildingLengthLimits();
      if (longestBuildingLength < limits.lowerLimit || longestBuildingLength > limits.upperLimit) {
        errs.longestBuildingLength = `Longest building length must lie between ${limits.lowerLimit} and ${limits.upperLimit} ft`;
      }
      if (shortestBuildingLength !== undefined && shortestBuildingLength !== null && longestBuildingLength < shortestBuildingLength) {
        errs.longestBuildingLength = 'Longest building length must be greater than or equal to shortest building length';
      }
    }

    const isPitched = this.isPitchedProduct();

    if (preferredSpan === undefined || preferredSpan === null) {
      if (isPitched) {
        errs.preferredSpan = 'Preferred span is required';
      }
    } else {
      const limits = this.getPreferredSpanLimits(productId);
      if (preferredSpan < limits.min || preferredSpan > limits.max) {
        errs.preferredSpan = `Preferred span must lie between ${limits.min} and ${limits.max} in`;
      }
      if (productId && rafterSpacingInches && preferredSpan) {
        if (!(isSMTiltPR(productId) || isMetalX(productId) || isSunframeMicroRail(productId))) {
          if (preferredSpan % rafterSpacingInches !== 0) {
            errs.preferredSpan = 'Preferred span must be a multiple of rafter spacing';
          }
        }
      }
    }

    if (rafterSpacingInches === undefined || rafterSpacingInches === null) {
      if (isPitched) {
        errs.rafterSpacingInches = 'Rafter spacing is required';
      }
    } else {
      const limits = getRafterSpacingLimits();
      if (rafterSpacingInches < limits.lowerLimit || rafterSpacingInches > limits.upperLimit) {
        errs.rafterSpacingInches = `Rafter spacing must lie between ${limits.lowerLimit} and ${limits.upperLimit} in`;
      }
    }

    if (parapetHeight !== undefined && parapetHeight !== null && parapetHeight !== '') {
      const val = Number(parapetHeight);
      if (!isNaN(val)) {
        if (val < 0 || val > 100) {
          errs.parapetHeight = 'Parapet height must lie between 0 and 100 in';
        }
      }
    }

    if (setbackDistance !== undefined && setbackDistance !== null) {
      const limits = getSetbackLimits();
      if (setbackDistance < limits.lowerLimit || setbackDistance > limits.upperLimit) {
        errs.setbackDistance = `Setback distance must lie between ${limits.lowerLimit} and ${limits.upperLimit} ft`;
      }
      if (buildingHeight !== undefined && buildingHeight !== null && setbackDistance >= buildingHeight / 2) {
        errs.setbackDistance = 'Setback distance must be less than half of building height';
      }
    }

    if (blockWeight !== undefined && blockWeight !== null) {
      if (blockWeight < 1 || blockWeight > 100) {
        errs.blockWeight = 'Ballast block weight must lie between 1 and 100 lbs';
      }
    }

    return errs;
  };

  componentDidMount() {
    this.applyDefaults();
  }

  componentDidUpdate(prevProps: Props) {
    // Re-seed when pitched/flat mode or roof type changes (rafter spacing
    // depends on roof type). Each applyDefaults only writes when the field
    // is empty, so existing DB values from the GET are never overwritten.
    if (prevProps.systemType !== this.props.systemType || prevProps.roofType !== this.props.roofType) {
      this.applyDefaults();
    }
    // When the user switches to the Ascender rail type, force the clamps to
    // the ascender-supported set: mid clamp is Universal AF only, end clamp
    // defaults to Universal AF (Pro is also valid). Existing DB values from a
    // different rail type (e.g. Standard) are not valid for ascender.
    if (prevProps.productId !== this.props.productId && isSMAscenderFlush(this.props.productId || 0)) {
      this.applyAscenderClampDefaults();
    }
    // Conversely, when switching FROM Ascender to a non-ascender SolarMount
    // product while Domestic Content is enabled, we must force the clamps
    // back to Standard. Normal SM products only support Standard clamps
    // when Domestic Content is YES.
    if (
      prevProps.productId !== this.props.productId &&
      isSMAscenderFlush(prevProps.productId || 0) &&
      isSolarMount(this.props.productId || 0) &&
      !isSMAscenderFlush(this.props.productId || 0) &&
      this.props.domesticContent
    ) {
      this.applyMidClampChange(MidEndClampChoice.STANDARD_CLAMP);
      this.setField('end_clamps_choices', MidEndClampChoice.STANDARD_CLAMP);
    }
    if (prevProps.domesticClampTarget !== this.props.domesticClampTarget && this.props.domesticClampTarget != null) {
      const target = this.props.domesticClampTarget;
      if (anyPanelsDrawn()) {
        this.setState({ pendingMidClamp: target, pendingFromDomestic: true });
      } else {
        this.applyMidClampChange(target);
        this.setField('end_clamps_choices', target);
        if (this.props.onDomesticClampResolved) {
          this.props.onDomesticClampResolved(true);
        }
      }
    }
  }

  // Lite spec defaults from docs/lite_default_values.md. Only fills fields
  // that are empty — GET-loaded values win.
  applyDefaults = () => {
    const isPitched = this.props.systemType === 'PITCHED';
    const empty = (v: any) => v === undefined || v === null || v === '';
    if (isPitched) {
      if (empty(this.props.buildingHeight)) this.setField('building_height', 15);
      if (empty(this.props.roofPitch)) this.setRoofPitch('14');
      if (empty(this.props.shortestBuildingLength)) this.setField('shortest_building_length', 20);
      // Pitched products are attached systems and don't use ballast blocks,
      // and `attachment_config` is redundant with the staggered_attachments
      // toggle below — both removed per Lite spec.
      if (empty(this.props.railDirection)) this.setField('rail_direction', 'EW');
      if (empty(this.props.rafterSpacingInches)) this.setField('rafter_spacing_inches', defaultRafterSpacing(this.props.roofType));
      if (empty(this.props.preferredSpan)) this.setField('preferred_span', 72);
      // Ascender rail: mid clamp is Universal AF only, end clamp defaults to
      // Universal AF (Pro is also valid). Only fills empty fields so GET-loaded
      // values win; the rail-type switch path (applyAscenderClampDefaults)
      // handles forcing when switching from a different rail type.
      if (isSMAscenderFlush(this.props.productId || 0)) {
        if (empty(this.props.midClampsChoices)) this.applyMidClampChange(MidEndClampChoice.UNIVERSAL_CLAMP);
        if (empty(this.props.endClampsChoices)) this.setField('end_clamps_choices', MidEndClampChoice.UNIVERSAL_CLAMP);
      }
    } else {
      if (empty(this.props.buildingHeight)) this.setField('building_height', 30);
      // Seed the active roof's per-roof pitch (not just the shared project-level
      // env.roof_pitch) so each roof carries its own slope — mirrors the pitched
      // branch above. Otherwise a roof with no explicit per-roof value falls back
      // to env.roof_pitch, which another roof's edit would overwrite.
      if (empty(this.props.roofPitch)) {
        const seed = !empty(this.props.envRoofPitch) ? Number(this.props.envRoofPitch)
          : !empty(this.props.liteRoofPitch) ? Number(this.props.liteRoofPitch)
            : 1;
        this.setRoofPitch(String(seed));
      }
      if (empty(this.props.parapetHeight)) this.setField('parapet_height_input', 12);
      if (empty(this.props.setbackDistance)) this.setField('setback_distance', 3);
      if (empty(this.props.longestBuildingLength)) this.setField('building_length', 100);
      if (empty(this.props.shortestBuildingLength)) this.setField('shortest_building_length', 20);
      if (empty(this.props.blockWeight)) this.setField('block_weight', 32);
      if (empty(this.props.attachmentOptimizationCriteria)) this.setField('attachment_optimization_criteria', AttachmentOptimization.MAXIMIZE_BALLAST);
    }
  };

  setField = (field: string, value: any) => {
    this.props.dispatch(SET_PROJECT_OPTION(field, value));
    if (field === 'setback_distance') {
      setTimeout(() => {
        try {
          drawRoofZonesOnStage();
          removePanelsInsideSetbackDistance();
        } catch (_) {}
      }, 0);
    }
  };

  // roof_pitch is stored per-roof on drawingManager.roofs[roofId].roofPitch in
  // legacy's data model. Lite has a single default roof at id 0; writing to env
  // would round-trip incorrectly because the legacy load path drops project-
  // level roof_pitch as redundant denormalisation (none of the per-product
  // env loaders pass it through). Write to per-roof so it survives reload.
  // Also write to state.tiltedRoof.roofPitch — savePanelsEditor() reads from
  // there and clobbers drawingManager.roofs[selectedRoofId].roofPitch on every
  // auto-save tick (drawingManagerReducer.ts:115); keeping the two in sync
  // stops the next 5s tick from reverting the user's pick.
  setRoofPitch = (value: string) => {
    const roofId = this.props.activeRoofId ?? 0;
    this.props.dispatch(SET_ROOF_ROOF_PITCH(roofId, value));
    this.props.dispatch(SET_TILTED_ROOF(value));
    this.setField('roof_pitch', value);
  };

  getNumericLimits = (field: string): { min?: number; max?: number } => {
    const toMinMax = (limits: { lowerLimit: number; upperLimit: number }) => ({
      min: limits.lowerLimit,
      max: limits.upperLimit,
    });

    const limitByField: { [fieldName: string]: () => { min?: number; max?: number } } = {
      building_height: () => toMinMax(getBuildingHeightLimits()),
      shortest_building_length: () => toMinMax(getShortestBuildingLengthLimits()),
      building_length: () => toMinMax(getLongestBuildingLengthLimits()),
      rafter_spacing_inches: () => toMinMax(getRafterSpacingLimits()),
      preferred_span: () => {
        const limits = this.getPreferredSpanLimits(this.props.productId);
        return { min: limits.min, max: limits.max };
      },
      parapet_height_input: () => ({ min: 0, max: 100 }),
      setback_distance: () => toMinMax(getSetbackLimits()),
      block_weight: () => ({ min: 1, max: 100 }),
    };

    const getLimits = limitByField[field];
    return getLimits ? getLimits() : {};
  };

  clampNumericValue = (field: string, value: number): number => {
    const { min, max } = this.getNumericLimits(field);
    if (min !== undefined && value < min) return min;
    if (max !== undefined && value > max) return max;
    return value;
  };

  onNumericField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      this.setField(field, undefined);
      return;
    }

    const value = Number(raw);
    if (Number.isNaN(value)) return;

    const { min, max } = this.getNumericLimits(field);

    // Do not allow values that are obviously outside hard input bounds.
    // Positive values below the minimum are normalized on blur so users can
    // still type multi-digit values like `30` when the lower limit is `15`.
    if ((min !== undefined && min >= 0 && value < 0) || (max !== undefined && value > max)) {
      return;
    }

    this.setField(field, value);
  };

  onNumericBlur = (field: string) => (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') return;

    const value = Number(raw);
    if (Number.isNaN(value)) return;

    const clampedValue = this.clampNumericValue(field, value);
    if (clampedValue !== value) {
      this.setField(field, clampedValue);
    }
    if ((field === 'preferred_span' || field === 'rafter_spacing_inches') && this.props.projectId) {
      const { dispatch, projectId } = this.props;
      dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId, regenBom: true }));
    }
  };

  onSelectField = (field: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setField(field, e.target.value);
  };

  onRoofPitchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    if (this.props.panelCount > 0) {
      this.setState({ pendingRoofPitch: newValue });
    } else {
      this.setRoofPitch(newValue);
    }
  };

  confirmRoofPitchChange = () => {
    const { pendingRoofPitch } = this.state;
    this.setState({ pendingRoofPitch: null }, () => {
      if (pendingRoofPitch) {
        this.props.dispatch(RESET_PANELS_STATE());
        this.setRoofPitch(pendingRoofPitch);
      }
    });
  };

  cancelRoofPitchChange = () => {
    this.setState({ pendingRoofPitch: null });
  };

  onTiltChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = Number(e.target.value);
    if (this.props.panelCount > 0) {
      this.setState({ pendingTilt: newValue });
    } else {
      this.setRoofPitch(String(newValue));
    }
  };

  confirmTiltChange = () => {
    const { pendingTilt } = this.state;
    this.setState({ pendingTilt: null }, () => {
      if (pendingTilt !== null) {
        this.props.dispatch(RESET_PANELS_STATE());
        this.setRoofPitch(String(pendingTilt));
      }
    });
  };

  cancelTiltChange = () => {
    this.setState({ pendingTilt: null });
  };

  onMidClampChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = Number(e.target.value);
    // Ascender mid clamp is Universal AF only and is decoupled from domestic
    // content — no panel-clear warning, no DC toggle.
    if (isSMAscenderFlush(this.props.productId || 0)) {
      this.applyMidClampChange(newValue);
      return;
    }
    const shouldSwitchDomesticOff = this.props.domesticContent && newValue !== MidEndClampChoice.STANDARD_CLAMP;
    if (anyPanelsDrawn()) {
      this.setState({ pendingMidClamp: newValue, pendingFromDomestic: false, pendingDomesticOff: shouldSwitchDomesticOff });
    } else {
      this.applyMidClampChange(newValue);
      if (shouldSwitchDomesticOff && this.props.onSwitchDomesticOff) {
        this.props.onSwitchDomesticOff();
      }
    }
  };

  onEndClampChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = Number(e.target.value);
    // Ascender end clamp changes do not affect domestic content — apply
    // directly without a DC-off confirmation modal.
    if (isSMAscenderFlush(this.props.productId || 0)) {
      this.setField('end_clamps_choices', newValue);
      return;
    }
    if (this.props.domesticContent && newValue !== MidEndClampChoice.STANDARD_CLAMP) {
      this.setState({ pendingEndClamp: newValue });
      return;
    }
    this.setField('end_clamps_choices', newValue);
  };

  confirmEndClampChange = () => {
    const { pendingEndClamp } = this.state;
    this.setState({ pendingEndClamp: null }, () => {
      if (pendingEndClamp !== null) {
        this.setField('end_clamps_choices', pendingEndClamp);
        if (this.props.onSwitchDomesticOff) {
          this.props.onSwitchDomesticOff();
        }
      }
    });
  };

  cancelEndClampChange = () => {
    this.setState({ pendingEndClamp: null });
  };

  clearAllPanelsFromAllRoofs = () => {
    this.props.dispatch(RESET_PANELS_STATE());
    this.props.dispatch(BOM_RESET());
    const s = getState() as any;
    const roofs = s.drawingManager && s.drawingManager.roofs;
    if (roofs) {
      Object.keys(roofs).forEach((roofId) => {
        this.props.dispatch(DELETE_PANELS_INSIDE_OF_ROOF(Number(roofId)));
      });
    }
  };

  applyMidClampChange = (value: number) => {
    this.setField('mid_clamps_choices', value);
    if (value === MidEndClampChoice.PRO_CLAMP) {
      this.props.dispatch(SET_ROW_SPACING(inchesToMeters(1)));
      this.props.dispatch(SET_COLUMN_SPACING(inchesToMeters(1)));
    } else if (value === MidEndClampChoice.STANDARD_CLAMP) {
      this.props.dispatch(SET_ROW_SPACING(inchesToMeters(0.25)));
      this.props.dispatch(SET_COLUMN_SPACING(inchesToMeters(0.25)));
    } else if (value === MidEndClampChoice.UNIVERSAL_CLAMP) {
      this.props.dispatch(SET_ROW_SPACING(inchesToMeters(0.5)));
      this.props.dispatch(SET_COLUMN_SPACING(inchesToMeters(0.5)));
    }
  };

  // Ascender rail supports only Universal AF mid clamps and {Pro, Universal}
  // end clamps. Force mid to Universal AF (with matching panel spacing) and
  // default end to Universal AF. Called when the user switches to the
  // ascender rail type.
  applyAscenderClampDefaults = () => {
    this.applyMidClampChange(MidEndClampChoice.UNIVERSAL_CLAMP);
    this.setField('end_clamps_choices', MidEndClampChoice.UNIVERSAL_CLAMP);
  };

  confirmMidClampChange = () => {
    const { pendingMidClamp, pendingFromDomestic, pendingDomesticOff } = this.state;
    this.setState({ pendingMidClamp: null, pendingFromDomestic: false, pendingDomesticOff: false }, () => {
      if (pendingMidClamp !== null) {
        this.clearAllPanelsFromAllRoofs();
        this.applyMidClampChange(pendingMidClamp);
        if (pendingFromDomestic) {
          this.setField('end_clamps_choices', pendingMidClamp);
          if (this.props.onDomesticClampResolved) {
            this.props.onDomesticClampResolved(true);
          }
        }
        if (pendingDomesticOff && this.props.onSwitchDomesticOff) {
          this.props.onSwitchDomesticOff();
        }
      }
    });
  };

  cancelMidClampChange = () => {
    const { pendingFromDomestic } = this.state;
    this.setState({ pendingMidClamp: null, pendingFromDomestic: false, pendingDomesticOff: false }, () => {
      if (pendingFromDomestic && this.props.onDomesticClampResolved) {
        this.props.onDomesticClampResolved(false);
      }
    });
  };

  onNumericSelectField = (field: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setField(field, Number(e.target.value));
  };

  onToggleField = (field: string) => (val: string) => {
    this.setField(field, val === 'Yes');
  };

  isPitchedProduct = () => {
    return this.props.systemType === 'PITCHED';
  };

  // stagger_attachments feeds the SM/NXT attachment count on the backend, so a
  // flip must regenerate the BOM. SET_PROJECT_OPTION alone only updates Redux +
  // marks the project dirty; the BOM GET returns the cached user_builds snapshot
  // and attachment quantities never recompute. Fire an immediate PUT + regen
  // (wipes user_builds, chains a fresh GET) so the change is reflected at once —
  // matching BomPanel's attachment dropdowns and the domestic-content toggle.
  //
  // Guard ONLY on projectId (mirrors BomPanel's attachment dropdowns). Do NOT
  // gate on liteUpdating: when the user flips stagger right after picking an
  // attachment type (Flashloc duo / standing seam / tile / tile replacement),
  // that selection's PUT+regen is still in flight (liteUpdating === true) and
  // the stagger regen would be silently skipped — leaving the attachment count
  // stale. The updateLiteProject epic is a switchMap, so a new request cancels
  // the in-flight one and re-runs with the latest state (stagger included).
  onToggleStaggerAttachments = (val: string): void => {
    const { dispatch, projectId } = this.props;
    dispatch(SET_PROJECT_OPTION('stagger_attachments', val === 'Yes'));
    if (projectId) {
      dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId, regenBom: true }));
    }
  };

  countRequiredEmpty = (): number => {
    const { buildingHeight } = this.props;
    let count = 0;
    if (!buildingHeight) count++;
    return count;
  };

  render() {
    const {
      buildingHeight, shortestBuildingLength, longestBuildingLength,
      staggerAttachments, parapetHeight,
      tilt, setbackDistance, blockWeight, attachmentOptimizationCriteria,
      roofPitch, railDirection, rafterSpacingInches, preferredSpan,
      productId, allowNorthBays, isHalfBlockAllowed, halfBlockWeight,
      domesticContent, hideRoofPitch,
      midClampsChoices, endClampsChoices,
      forceOpenSignal, buildingCode,
    } = this.props;

    const errors = this.computeErrors();
    const isPitched = this.isPitchedProduct();
    // SM/NXT roof pitch starts at 9° under ASCE 7-16 / 7-22.
    const pitchOptions = isASCE716or722(buildingCode || 0)
      ? PITCHED_ROOF_PITCH_OPTIONS.filter((p) => Number(p.value) >= 9)
      : PITCHED_ROOF_PITCH_OPTIONS;
    const isAscender = isSMAscenderFlush(productId || 0);
    const midClampOptions = isAscender ? ASCENDER_MID_CLAMP_OPTIONS : SM_CLAMP_OPTIONS;
    const endClampOptions = isAscender ? ASCENDER_END_CLAMP_OPTIONS : SM_CLAMP_OPTIONS;
    const badgeCount = this.countRequiredEmpty();

    return (
      <React.Fragment>
      <V3CollapsibleSection
        title="Advanced"
        icon={AdvancedIcon}
        iconVariant="orange"
        defaultOpen={false}
        badgeCount={badgeCount}
        forceOpenSignal={forceOpenSignal}
      >
        <V3SpecsTable flush={true}>
          {/* ── Shared: Building Height ── */}
          <V3SpecRow label="Building Height" required={!buildingHeight}>
            <V3SpecFieldWrap>
              <input
                className={`v3-spec-inline-input${errors.buildingHeight ? ' v3-spec-val-error' : ''}`}
                type="number"
                placeholder="—"
                style={{ width: 46 }}
                value={buildingHeight ?? ''}
                min={this.getNumericLimits('building_height').min}
                max={this.getNumericLimits('building_height').max}
                onChange={this.onNumericField('building_height')}
                onBlur={this.onNumericBlur('building_height')}
              />
              <span className="v3-spec-unit">ft</span>
            </V3SpecFieldWrap>
          </V3SpecRow>
          {errors.buildingHeight && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.buildingHeight}</span>
            </V3SpecRow>
          )}

          {/* ── Pitched (NXT/SM) — matches v6 design ── */}
          {isPitched && (
            <React.Fragment>
              {!hideRoofPitch && (
              <V3SpecRow label="Roof Pitch">
                <V3SpecFieldWrap>
                  <select
                    className="v3-spec-inline-select"
                    style={{ width: 80 }}
                    value={roofPitch || ''}
                    onChange={this.onRoofPitchChange}
                  >
                    <option value="" disabled>Select</option>
                    {pitchOptions.map((p) => (
                      <option key={p.value} value={p.value}>{p.name}</option>
                    ))}
                  </select>
                </V3SpecFieldWrap>
              </V3SpecRow>
              )}

              <V3SpecRow label="Shortest Bldg Length">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.shortestBuildingLength ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={shortestBuildingLength ?? ''}
                    min={this.getNumericLimits('shortest_building_length').min}
                    max={this.getNumericLimits('shortest_building_length').max}
                    onChange={this.onNumericField('shortest_building_length')}
                    onBlur={this.onNumericBlur('shortest_building_length')}
                  />
                  <span className="v3-spec-unit">ft</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.shortestBuildingLength && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.shortestBuildingLength}</span>
                </V3SpecRow>
              )}

              <V3SpecRow label="Stagger Attachments">
                <V3UnitToggle
                  options={['Yes', 'No']}
                  value={staggerAttachments ? 'Yes' : 'No'}
                  onChange={this.onToggleStaggerAttachments}
                />
              </V3SpecRow>

              <V3SpecRow label="Rail Direction">
                <V3SpecFieldWrap>
                  <select
                    className="v3-spec-inline-select"
                    style={{ width: 110 }}
                    value={railDirection || ''}
                    onChange={this.onSelectField('rail_direction')}
                  >
                    <option value="" disabled>Select</option>
                    {RAIL_DIRECTION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                </V3SpecFieldWrap>
              </V3SpecRow>

              <V3SpecRow label="Preferred Span">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.preferredSpan ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={preferredSpan ?? ''}
                    min={this.getNumericLimits('preferred_span').min}
                    max={this.getNumericLimits('preferred_span').max}
                    onChange={this.onNumericField('preferred_span')}
                    onBlur={this.onNumericBlur('preferred_span')}
                  />
                  <span className="v3-spec-unit">in</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.preferredSpan && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.preferredSpan}</span>
                </V3SpecRow>
              )}

              <V3SpecRow label="Rafter/Rib/Seam Spacing">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.rafterSpacingInches ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={rafterSpacingInches ?? ''}
                    min={this.getNumericLimits('rafter_spacing_inches').min}
                    max={this.getNumericLimits('rafter_spacing_inches').max}
                    onChange={this.onNumericField('rafter_spacing_inches')}
                    onBlur={this.onNumericBlur('rafter_spacing_inches')}
                  />
                  <span className="v3-spec-unit">in</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.rafterSpacingInches && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.rafterSpacingInches}</span>
                </V3SpecRow>
              )}

              {isSolarMount(productId || 0) && (
                <React.Fragment>
                  <V3SpecRow label="Mid Clamp">
                    <V3SpecFieldWrap>
                      <select
                        className="v3-spec-inline-select"
                        style={{ width: 130 }}
                        value={midClampsChoices ?? ''}
                        onChange={this.onMidClampChange}
                      >
                        <option value="" disabled>Select</option>
                        {midClampOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.name}</option>
                        ))}
                      </select>
                    </V3SpecFieldWrap>
                  </V3SpecRow>

                  <V3SpecRow label="End Clamp">
                    <V3SpecFieldWrap>
                      <select
                        className="v3-spec-inline-select"
                        style={{ width: 130 }}
                        value={endClampsChoices ?? ''}
                        onChange={this.onEndClampChange}
                      >
                        <option value="" disabled>Select</option>
                        {endClampOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.name}</option>
                        ))}
                      </select>
                    </V3SpecFieldWrap>
                  </V3SpecRow>
                </React.Fragment>
              )}
            </React.Fragment>
          )}

          {/* ── Flat (RM10/GF10/GF5) — matches v6 design ── */}
          {!isPitched && (
            <React.Fragment>
              {!hideRoofPitch && (
              <V3SpecRow label="Roof Pitch">
                <V3SpecFieldWrap>
                  <select
                    className="v3-spec-inline-select"
                    style={{ width: 90 }}
                    value={this.props.flatRoofPitch ?? ''}
                    onChange={this.onTiltChange}
                  >
                    <option value="" disabled>Select</option>
                    {FLAT_ROOF_PITCH_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                </V3SpecFieldWrap>
              </V3SpecRow>
              )}

              <V3SpecRow label="Parapet Height">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.parapetHeight ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={parapetHeight ?? ''}
                    min={this.getNumericLimits('parapet_height_input').min}
                    max={this.getNumericLimits('parapet_height_input').max}
                    onChange={this.onNumericField('parapet_height_input')}
                    onBlur={this.onNumericBlur('parapet_height_input')}
                  />
                  <span className="v3-spec-unit">in</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.parapetHeight && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.parapetHeight}</span>
                </V3SpecRow>
              )}

              <V3SpecRow label="Setback Distance">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.setbackDistance ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={setbackDistance ?? ''}
                    min={this.getNumericLimits('setback_distance').min}
                    max={this.getNumericLimits('setback_distance').max}
                    onChange={this.onNumericField('setback_distance')}
                    onBlur={this.onNumericBlur('setback_distance')}
                  />
                  <span className="v3-spec-unit">ft</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.setbackDistance && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.setbackDistance}</span>
                </V3SpecRow>
              )}

              <V3SpecRow label="Longest Bldg Length">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.longestBuildingLength ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={longestBuildingLength ?? ''}
                    min={this.getNumericLimits('building_length').min}
                    max={this.getNumericLimits('building_length').max}
                    onChange={this.onNumericField('building_length')}
                    onBlur={this.onNumericBlur('building_length')}
                  />
                  <span className="v3-spec-unit">ft</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.longestBuildingLength && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.longestBuildingLength}</span>
                </V3SpecRow>
              )}

              {/* Shortest Bldg Length — default 20 ft on blank page;
                  auto-fills from canvas once the user draws a roof. */}
              <V3SpecRow label="Shortest Bldg Length">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.shortestBuildingLength ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={shortestBuildingLength ?? ''}
                    min={this.getNumericLimits('shortest_building_length').min}
                    max={this.getNumericLimits('shortest_building_length').max}
                    onChange={this.onNumericField('shortest_building_length')}
                    onBlur={this.onNumericBlur('shortest_building_length')}
                  />
                  <span className="v3-spec-unit">ft</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.shortestBuildingLength && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.shortestBuildingLength}</span>
                </V3SpecRow>
              )}

              <V3SpecRow label="Ballast Block Weight">
                <V3SpecFieldWrap>
                  <input
                    className={`v3-spec-inline-input${errors.blockWeight ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder="—"
                    style={{ width: 46 }}
                    value={blockWeight ?? ''}
                    min={this.getNumericLimits('block_weight').min}
                    max={this.getNumericLimits('block_weight').max}
                    onChange={this.onNumericField('block_weight')}
                    onBlur={this.onNumericBlur('block_weight')}
                  />
                  <span className="v3-spec-unit">lbs</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.blockWeight && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.blockWeight}</span>
                </V3SpecRow>
              )}

              {/* Use Special North Bay — RM10-EVO exclusive. Persists as 1/0 to backend
                  PositiveSmallIntegerField allow_north_bays. Engineering report's North
                  Bay Factor Map layout activates when this is on. */}
              {isRM10Evolution(productId || 0) && !domesticContent && (
                <V3SpecRow label="Use Special North Bay">
                  <V3UnitToggle
                    options={['Yes', 'No']}
                    value={allowNorthBays ? 'Yes' : 'No'}
                    onChange={(val) => this.setField('allow_north_bays', val === 'Yes' ? 1 : 0)}
                  />
                </V3SpecRow>
              )}

              <V3SpecRow label="Attachment Config.">
                <V3SpecFieldWrap>
                  <select
                    className="v3-spec-inline-select"
                    style={{ width: 130 }}
                    value={attachmentOptimizationCriteria ?? ''}
                    onChange={this.onNumericSelectField('attachment_optimization_criteria')}
                  >
                    <option value="" disabled>Select</option>
                    {FLAT_ATTACHMENT_CONFIG_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                </V3SpecFieldWrap>
              </V3SpecRow>
            </React.Fragment>
          )}
        </V3SpecsTable>
      </V3CollapsibleSection>
      {this.state.pendingRoofPitch && (
        <div className="v3-modal-overlay" onClick={this.cancelRoofPitchChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Change Roof Pitch?</div>
            </div>
            <div className="v3-modal-body">
              <div className="v3-modal-subtitle">
                Changing the roof pitch will clear all panels in the current layout. This cannot be undone.
              </div>
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelRoofPitchChange}>
                Cancel
              </V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmRoofPitchChange}>
                Continue
              </V3Button>
            </div>
          </div>
        </div>
      )}
      {this.state.pendingTilt !== null && (
        <div className="v3-modal-overlay" onClick={this.cancelTiltChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Change Roof Pitch?</div>
            </div>
            <div className="v3-modal-body">
              <div className="v3-modal-subtitle">
                Changing the roof pitch will clear all panels in the current layout. This cannot be undone.
              </div>
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelTiltChange}>
                Cancel
              </V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmTiltChange}>
                Continue
              </V3Button>
            </div>
          </div>
        </div>
      )}
      {this.state.pendingMidClamp !== null && (
        <div className="v3-modal-overlay" onClick={this.cancelMidClampChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Clear all panels?</div>
            </div>
            <div className="v3-modal-body">
              {this.state.pendingFromDomestic && this.state.pendingMidClamp === MidEndClampChoice.STANDARD_CLAMP ? (
                <div className="v3-modal-subtitle">
                  <p>Changing SM to DC clamps will remove all panels across all roof areas due to the different clamp spacing. This cannot be undone.</p>
                  <p>Thank you for your patience as we work on this issue.</p>
                </div>
              ) : this.state.pendingFromDomestic && this.state.pendingMidClamp === MidEndClampChoice.UNIVERSAL_CLAMP ? (
                <div className="v3-modal-subtitle">
                  <p>Changing SM to non-DC clamps will remove all panels across all roof areas due to the different clamp spacing. This cannot be undone.</p>
                  <p>Thank you for your patience as we work on this issue.</p>
                </div>
              ) : this.props.domesticContent ? (
                <div className="v3-modal-subtitle">
                  The {this.state.pendingMidClamp === MidEndClampChoice.UNIVERSAL_CLAMP ? 'Universal AF' : 'PRO Series'} clamps are not available in DC. Changing to this clamp will remove all panels across all roof areas due to the different clamp spacing. This cannot be undone.
                </div>
              ) : (
                <div className="v3-modal-subtitle">
                  Changing the clamp changes the clamp spacing, so all panels across all roof areas need to be deleted. This cannot be undone.
                </div>
              )}
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelMidClampChange}>
                Cancel
              </V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmMidClampChange}>
                Continue
              </V3Button>
            </div>
          </div>
        </div>
      )}
      {this.state.pendingEndClamp !== null && (
        <div className="v3-modal-overlay" onClick={this.cancelEndClampChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Turn off Domestic Content?</div>
            </div>
            <div className="v3-modal-body">
              <div className="v3-modal-subtitle">
                The {this.state.pendingEndClamp === MidEndClampChoice.UNIVERSAL_CLAMP ? 'Universal AF' : 'PRO Series'} clamps are not available in DC. Changing to this clamp will turn off Domestic Content.
              </div>
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelEndClampChange}>
                Cancel
              </V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmEndClampChange}>
                Continue
              </V3Button>
            </div>
          </div>
        </div>
      )}
      </React.Fragment>
    );
  }
}

// mapStateToProps reads all the fields we care about from projectEnvConfig.
// Fields that are hidden from the UI (roof_type, mid/end_clamps_choices,
// attachment_type) are NOT destructured here — they're
// populated via product defaults + the 4-step modal handoff and flow
// directly to the save payload.
function mapStateToProps(appState: appState) {
  const pc = appState.projectConfiguration || ({} as any);
  const env = pc.projectEnvConfig || ({} as any);
  // system_type is frontend UX metadata — not in UbLiteProjectSerializer's
  // field allow-list, so it's silently dropped by the backend on create and
  // absent from GET responses. Derive from the product FK instead: the two
  // Lite pitched products are nxt_umount (pk=9) and solarmount_2
  // (pk=9 → solarmount_2); RM10/GF10/GF5 are flat.
  const productId = env.product ?? env.product_id ?? env.productId ?? pc.productId;
  const isPitchedProduct = isSMFlushOrTiltOrTiltPR(productId) || isNxtHorizon(productId);
  const derivedSystemType = productId
    ? (isPitchedProduct ? 'PITCHED' : 'FLAT')
    : '';
  // roof_pitch is per-roof in legacy's data model (drawingManager.roofs[id].roofPitch).
  // Read from the currently active roof (selectedRoofId from background state),
  // falling back to the first available roof for the default view.
  const dm = (appState as any).drawingManager || {};
  const roofs = dm.roofs || {};
  const bg = appState.background || {} as any;
  const activeRoofId = bg.selectedRoofId ?? null;
  // Satellite (Google Maps) view is active when a real map provider is set
  // (anything other than BLANK). While in that view with no roof selected,
  // the user is in the roof-marking sub-mode — not inside a roof — so the
  // per-roof pitch field is hidden (mirrors DesignWorkspace's noActiveRoof).
  const mapType = (appState as any).roofsSelector && (appState as any).roofsSelector.mapType;
  const isSatellite = !!mapType && mapType !== MAP_TYPE.BLANK;
  const hideRoofPitch = isSatellite && !activeRoofId;
  const activeRoof = (activeRoofId !== null && roofs[activeRoofId])
    ? roofs[activeRoofId]
    : roofs[0] || roofs[Object.keys(roofs)[0]] || {};

  let perRoofPitch = (activeRoof.roofPitch !== undefined && activeRoof.roofPitch !== null) ? String(activeRoof.roofPitch) : '';

  // Google Maps initial state: no roofs exist yet. Fall back to the active
  // tiltedRoof state (synced by DesignWorkspace) or a residential default.
  if (!perRoofPitch && derivedSystemType === 'PITCHED') {
    const tr = (appState as any).tiltedRoof;
    perRoofPitch = (tr && tr.roofPitch && tr.roofPitch !== '---') ? tr.roofPitch : '14';
  }

  // Flat (RM/GF) roof pitch is per-roof too — read the active roof's slope so
  // editing one roof's pitch doesn't change what the others display. The write
  // path (setRoofPitch) also writes the shared project-level env.roof_pitch;
  // reading it here (as the old code did) leaked one roof's value onto every
  // roof. Fall back to the per-roof lite value, then the project-level default.
  const rawFlatPitch = activeRoof.roofPitch;
  const flatPitchNum = (rawFlatPitch !== undefined && rawFlatPitch !== null
    && String(rawFlatPitch) !== '' && String(rawFlatPitch) !== '---')
    ? Number(rawFlatPitch)
    : NaN;
  const flatRoofPitch = !isNaN(flatPitchNum)
    ? flatPitchNum
    : (activeRoof.liteRoofPitch ?? env.roof_pitch);
  return {
    systemType: env.system_type || derivedSystemType,
    roofType: env.roof_type || '',
    buildingHeight: env.building_height,
    shortestBuildingLength: env.shortest_building_length,
    longestBuildingLength: env.building_length,
    staggerAttachments: !!env.stagger_attachments,
    parapetHeight: env.parapet_height_input,
    tilt: env.tilt,
    setbackDistance: env.setback_distance,
    blockWeight: env.block_weight,
    attachmentOptimizationCriteria: env.attachment_optimization_criteria,
    roofPitch: perRoofPitch,
    liteRoofPitch: activeRoof.liteRoofPitch,
    envRoofPitch: env.roof_pitch,
    flatRoofPitch,
    railDirection: env.rail_direction || '',
    rafterSpacingInches: env.rafter_spacing_inches,
    preferredSpan: env.preferred_span,
    productId,
    allowNorthBays: !!env.allow_north_bays,
    domesticContent: !!env.domestic_content,
    isHalfBlockAllowed: !!env.is_half_block_allowed,
    halfBlockWeight: env.half_block_weight,
    panelCount: appState.panels && appState.panels.panels ? appState.panels.panels.length : 0,
    activeRoofId,
    hideRoofPitch,
    midClampsChoices: env.mid_clamps_choices,
    endClampsChoices: env.end_clamps_choices,
    buildingCode: env.building_code,
  };
}

export default connect(mapStateToProps)(AdvancedSection);
